import type { WineCardData, WineIntelligenceCard } from "./types";

function pick(row: Record<string, unknown>, keys: string[]): string | undefined {
  for (const k of keys) {
    const v = row[k];
    if (v !== undefined && v !== null && String(v).trim() !== "") {
      return String(v);
    }
  }
  return undefined;
}

/**
 * Map the structured WineIntelligenceCard (22-field AI response with
 * { value, estimated } fields) into the flat WineCardData shape the widget
 * renders. Prefer this over rowToWineCard when data.card is available.
 */
export function cardToWineCard(card: WineIntelligenceCard): WineCardData {
  const v = <T>(field?: { value: T }): T | undefined => field?.value;
  const s = (field?: { value: unknown }): string | undefined => {
    const val = field?.value;
    if (val === undefined || val === null) return undefined;
    const str = String(val).trim();
    return str !== "" ? str : undefined;
  };

  return {
    name: s(card.wine),
    producer: s(card.producer),
    region: s(card.region),
    appellation: s(card.appellation),
    vintage: s(card.vintage),
    rating: v(card.rating),
    style: s(card.wineStyle),
    grapes: s(card.grapes),
    aging: s(card.aging),
    oak: s(card.oakMonths),
    bottleFormat: s(card.bottleFormatMl),
    drinkingWindow: s(card.drinkingWindow),
    peak: s(card.peak),
    priceRange: s(card.priceRange),
    rarity: v(card.rarity),
    investmentGrade: s(card.investmentGrade),
    cruLevel: s(card.cruLevel),
    collectorDemand: s(card.collectorDemand),
    marketLiquidity: s(card.marketLiquidity),
    investmentSignal: card.investmentSignal,
    description: card.verifiedSummary,
    tastingNotes: card.investmentThesis,
  };
}

/**
 * Map an arbitrary wine row (e.g., from Supabase wines_core_v2) into the
 * normalized WineCardData shape the widget renders. Tries multiple common
 * column-name variants for each field so callers don't have to pre-normalize.
 */
export function rowToWineCard(row: Record<string, unknown>): WineCardData {
  return {
    name: pick(row, ["wine_name", "name", "wine", "full_name"]),
    producer: pick(row, ["producer", "winery", "house"]),
    region: pick(row, ["region", "country_region", "country"]),
    appellation: pick(row, ["appellation"]),
    vintage: pick(row, ["vintage", "year"]),
    rating: pick(row, ["rating", "score", "wine_rating"]),
    style: pick(row, ["wine_style", "style"]),
    grapes: pick(row, ["grapes", "grape_varieties", "varietals"]),
    alcohol: pick(row, ["alcohol", "abv"]),
    aging: pick(row, ["aging"]),
    oak: pick(row, ["oak"]),
    bottleFormat: pick(row, ["bottle_format", "format"]),
    drinkingWindow: pick(row, ["drinking_window", "drink_window"]),
    peak: pick(row, ["peak", "peak_year"]),
    priceRange: pick(row, ["price_range", "price_estimate", "price"]),
    rarity: pick(row, ["rarity", "rarity_score"]),
    investmentGrade: pick(row, ["investment_grade"]),
    cruLevel: pick(row, ["cru_level", "cru"]),
    collectorDemand: pick(row, ["collector_demand", "demand"]),
    marketLiquidity: pick(row, ["market_liquidity", "liquidity"]),
    investmentSignal: pick(row, ["investment_signal", "signal"]),
    tastingNotes: pick(row, ["tasting_notes", "notes"]),
    description: pick(row, ["description", "summary"]),
  };
}
